/**
 * Minimal Excel-numberFormat → JS formatter.
 *
 * Covers the patterns the canonical Arcanum models actually use today:
 *   - `"$#,##0"` / `"$#,##0.00"` — currency 0 dp / 2 dp
 *   - `"0%"` / `"0.0%"` / `"0.00%"` — percentage with 0/1/2 dp
 *   - `"#,##0"` — integer with thousands separators
 *   - `"#,##0.00x"` — multiple (DSCR, equity multiple) — added 2026-04-25
 *     after Phase 3 `/review` Pass 3 surfaced raw-number rendering on K7/K8
 *   - `"_(* #,##0_);_(* (#,##0);_(* \"-\"??_);_(@_)"` — Excel accounting
 *     integer format (parentheses for negatives, dash for zero) — added
 *     2026-04-25 (heavy use across the multifamily-v1 cost columns G8, H8)
 *   - `"_(* #,##0.00_);..."` — accounting decimal variant
 *   - `"m/d/yyyy"` (or any string containing y/m/d) — Excel serial date
 *
 * Anything outside those falls through to `String(value)` — the canonical
 * formatter cannot become a general-purpose Excel format engine without a
 * heavyweight dep. New format-string patterns get added here as the canonical
 * models grow.
 *
 * Used exclusively by `formatCellValue` in `components/builder/model-view/
 * cell-style.ts` (in-platform-model-view Phase 3 Session 3).
 */

/**
 * `value` is the raw cell value from `cellValues` (already a JS number for
 * formula/output cells per the server-side resolution path). `format` is the
 * Excel format string captured at ingest time on `GridCellStyle.numberFormat`.
 */
export function formatExcelNumber(value: number, format: string): string {
  // Currency — `$#,##0` (0 dp) or `$#,##0.00` (2 dp). Bare `#,##0` (no `$`)
  // is handled by the integer branch below; bare-with-decimals `#,##0.00`
  // is treated as integer-with-decimals by Intl, which is the right call
  // because there's no currency context.
  if (format === '$#,##0' || format === '$#,##0.00') {
    const decimals = format === '$#,##0.00' ? 2 : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  // Percentage — `0%`, `0.0%`, `0.00%`, etc. The decimal-zeros run controls
  // precision (e.g. `0.00%` → 2 dp). Excel multiplies the value by 100; we
  // do the same so a stored `0.058` renders as `5.80%`.
  if (/^0(\.0+)?%$/.test(format)) {
    const m = format.match(/\.(0+)/);
    const decimals = m ? m[1].length : 0;
    return `${(value * 100).toFixed(decimals)}%`;
  }

  // Integer / thousands-separated.
  if (format === '#,##0') {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  // Multiple — `#,##0.00x` or `#,##0x` (DSCR `1.20x`, equity multiple `2.50x`).
  // The trailing literal `x` is the Excel idiom for "multiple of 1×". Decimal
  // count comes from the `.0+` group, same as the percent path.
  {
    const m = format.match(/^#,##0(\.0+)?x$/);
    if (m) {
      const decimals = m[1] ? m[1].length - 1 : 0;
      return `${value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}x`;
    }
  }

  // Excel accounting formats — the canonical 4-section format string is
  // `_(* #,##0_);_(* (#,##0);_(* "-"??_);_(@_)` (or `.00` variant for
  // decimals). The 4 sections are positive/negative/zero/text. We render
  // positive numbers with thousands separators, negative numbers with
  // parentheses (Excel's accounting convention), and zero as a dash —
  // matching what users see in the source workbook. Decimal precision
  // comes from `.0+` presence in the positive section (1+ zeros).
  //
  // Phase 2 (model-view-fidelity, 2026-04-26): regex extended to also match
  // dollar-prefixed accounting variants `_("$"* #,##0_);...` (110 cells in
  // canonical multifamily-v1, mostly DevBudget + UnitMix). Decimal regex
  // relaxed from `.00` to `.0+` so the single-decimal `_(* #,##0.0_);...`
  // variant emits `12.3` instead of `12`.
  if (/^_\((?:\*|"\$"\*)\s*#,##0/.test(format)) {
    const dollarPrefixed = format.startsWith('_("$"');
    const decimalMatch = format.match(/#,##0\.(0+)/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;
    if (value === 0) return dollarPrefixed ? '$ -' : '-';
    const opts = {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };
    const formatted = Math.abs(value).toLocaleString('en-US', opts);
    const prefixed = dollarPrefixed ? `$${formatted}` : formatted;
    return value < 0 ? `(${prefixed})` : prefixed;
  }

  // Red-colored negative currency — e.g. `"$"#,##0.00_);[Red]("$"#,##0.00)`
  // (1211 cells in canonical multifamily-v1, all on LoanAmortization). The
  // `[Red]` color directive is non-essential for fidelity (we surface the
  // sign via parens). Format positive as `$1,234.56`, negative as
  // `($1,234.56)`. Decimal precision from `.0+` in the positive section.
  // Added by model-view-fidelity Phase 2 (2026-04-26).
  if (/^"\$"#,##0/.test(format) && /\[Red\]/.test(format)) {
    const decimalMatch = format.match(/#,##0\.(0+)/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;
    const opts = {
      style: 'currency' as const,
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };
    if (value === 0) {
      return new Intl.NumberFormat('en-US', opts).format(0);
    }
    const formatted = new Intl.NumberFormat('en-US', opts).format(
      Math.abs(value)
    );
    return value < 0 ? `(${formatted})` : formatted;
  }

  // Locale-prefixed short-month + 2-digit-year date — e.g.
  // `[$-409]mmm-yy;@` (432 cells across MCF / ACF / LoanAmortization
  // header rows). Excel renders this as `Apr-26`. The general date branch
  // below would emit `4/30/2026` instead — handled separately here for
  // fidelity. The `[$-409]` is the US English locale code and can be ignored.
  // Added by model-view-fidelity Phase 2 (2026-04-26).
  if (/mmm[-\s]?yy/i.test(format)) {
    const date = new Date((value - 25569) * 86400 * 1000);
    if (Number.isNaN(date.getTime())) return String(value);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.toLocaleDateString('en-US', { year: '2-digit' });
    return `${month}-${year}`;
  }

  // Unit-suffix formats — a numeric spec followed by a quoted literal suffix,
  // e.g. `0 "Months"`, `0 "Years"`, `0"-Story"`, `0.0 "Years"`. MUST run before
  // the date branch below (the suffix text contains `m`/`y`, which would
  // otherwise misroute to the Excel-serial date path). The quoted segments are
  // literal text; the inter-token spaces are preserved.
  {
    const unitMatch = format.match(/^(0(?:\.0+)?)((?:\s|"[^"]*")+)$/);
    if (unitMatch) {
      const decimals = (unitMatch[1].split('.')[1] || '').length;
      const suffix = unitMatch[2].replace(/"/g, '');
      return `${value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix}`;
    }
  }

  // Custom 4-section percent — `0.0%_);(0.0%);-_);@_)` (positive ; negative in
  // parens ; zero as dash ; text). The simple `^0(\.0+)?%$` branch above is
  // `$`-anchored so it never matches this multi-section form. Decimals come
  // from the leading `0.0%` run.
  {
    const cpMatch = format.match(/^0(\.0+)?%[^;]*;\(0(?:\.0+)?%\)[^;]*;-/);
    if (cpMatch) {
      const decimals = (cpMatch[1] || '').replace('.', '').length;
      if (value === 0) return '-';
      const pct = (Math.abs(value) * 100).toFixed(decimals);
      return value < 0 ? `(${pct}%)` : `${pct}%`;
    }
  }

  // Date formats — anything containing `y`, `m`, or `d` triggers the
  // Excel-serial → JS Date path. `25569` is the days-since-1900-01-01 offset
  // for 1970-01-01 (the Unix epoch), accounting for the Excel 1900-leap-year
  // bug. Multiply by 86400 × 1000 to get ms.
  if (/[ymd]/.test(format)) {
    const date = new Date((value - 25569) * 86400 * 1000);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-US');
  }

  // Unknown format — return the raw value's string form. Future format
  // patterns get added as branches above.
  return String(value);
}
